import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import { useAuthStore } from '~/stores/auth'

const API_URL = import.meta.env.VITE_API_URL

const axiosClient = axios.create({
  baseURL: API_URL,
  timeout: 120000,
})

axiosClient.interceptors.request.use((config) => {
  const authStore = useAuthStore()
  if (authStore.accessToken) {
    config.headers.Authorization = `Bearer ${authStore.accessToken}`
  }
  return config
})

export interface RequestConfig<TData = unknown> {
  url?: string
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE'
  params?: object
  data?: TData
  headers?: AxiosRequestConfig['headers']
  signal?: AbortSignal
}

export interface ResponseConfig<TData = unknown> {
  data: TData
  status: number
  statusText: string
  headers: AxiosResponse['headers']
}

export interface ResponseErrorConfig<TError = unknown> {
  data: TError
  status: number
  statusText: string
  headers: AxiosResponse['headers']
}

export async function client<TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> {
  const response = await axiosClient.request<TData>({
    url: config.url,
    method: config.method,
    params: config.params,
    data: config.data,
    headers: config.headers,
    signal: config.signal,
  })
  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  }
}

export default client
